import { Text, Box, Button, Checkbox, Flex, FormControl, Input, Link } from '@chakra-ui/react'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from '@tanstack/react-query'
import { fetchUserByAddress, fetchUserByUsername } from '@/api/query'
import { useTranslation, Trans } from 'next-i18next/pages'
import { isAsciiGraphic } from '@/helpers/asciiValidation'
import { useEffect, useState } from 'react'
import { useReferrer } from '@/hooks/useReferral'
import { useSignInModal } from '../SigninHostModal'
import { StageTitle } from './StageTitle'
import { OperationType, StatusView } from './StatusView'
import { avatarListComponent } from '@/components/molecules/AvatarSet'
import dynamic from 'next/dynamic'
import { UserProfile } from '@/api/UserProfileContract.types'
import { getProfiles, Profile } from 'thirdweb/wallets'
import { thirdwebClient } from '@/services/wallet-service'
import { uploadImage } from '@/components/molecules/EditorAvatar'

const EditorAvatarComponent = dynamic(
  () => import('@/components/molecules/EditorAvatar').then((res) => res.EditorAvatar),
  {
    ssr: false,
  }
)

const getFormSchema = (t: any) =>
  yup
    .object({
      user_name: yup
        .string()
        .label(t('account.form.nickname.label'))
        .test('isMinLength', t('account.form.nickname.min-length'), (str) => (str ? str.length >= 3 : true))
        .test('isMaxLength', t('account.form.nickname.max-length'), (str) => (str ? str.length <= 12 : true))
        .test('isAlphaNumeric', t('account.form.nickname.invalid'), (str) => (str ? isAsciiGraphic(str) : true))
        .test('isStartWith', t('account.form.nickname.no-dollar'), (value) => {
          return !value?.startsWith('$')
        }),
      avatar: yup.string().required(),
      agreement: yup.boolean().oneOf([true], t('account.form.agreement-required')),
      referral_code: yup.string().optional(),
    })
    .required()

export const RegistrationForm = ({
  withoutBackButton = false,
  initialFields,
  handleConfirm,
}: {
  withoutBackButton?: boolean
  initialFields?: { username?: string; avatar?: string; wallet: string }
  handleConfirm?: (profile: UserProfile) => Promise<void>
}) => {
  const { handleRegister, navigateTo, context } = useSignInModal()
  const [isProcessing, setProcessing] = useState(false)
  const { t } = useTranslation()
  const formSchema = getFormSchema(t)
  const {
    register,
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      user_name: initialFields?.username ? initialFields?.username : '',
      avatar: initialFields?.avatar ? initialFields?.avatar : avatarListComponent.at(0),
      referral_code: '',
      agreement: true,
    },
    resolver: yupResolver(formSchema),
  })

  const { referrer, campaign } = useReferrer()

  useEffect(() => {
    if (context.wallet?.includes('social')) {
      getProfiles({
        client: thirdwebClient,
      }).then((res) => {
        const profile = getUserParams(res.at(0))

        if (profile?.avatar) {
          fetch(profile.avatar)
            .then((res) => {
              if (!res.ok) {
                throw new Error()
              }

              return res.blob()
            })
            .then((blob) => uploadImage(blob))
            .then((cid) => {
              setValue('avatar', cid)
            })
            .catch(() => null)
        }

        if (profile?.user_name) {
          setValue('user_name', parseUsername(profile.user_name), { shouldValidate: true })
        }
      })
    }
  }, [])

  useEffect(() => {
    const r = referrer

    if (!r) {
      return
    }

    fetchUserByAddress(r)
      .then((res) => {
        setValue('referral_code', res?.profile.user_name)
      })
      .catch(() => null)
  }, [referrer])

  const avatar = useWatch({ name: 'avatar', control })

  const handleFormSumbit = async () => {
    const formValue = getValues()
    const [name, referral] = await Promise.all([
      fetchUserByUsername(formValue.user_name),
      fetchUserByUsername(formValue.referral_code),
    ])

    if (!!name) {
      setError('user_name', { message: t('account.form.nickname.already-taken') })
      return
    }

    setProcessing(true)

    if (handleConfirm) {
      await handleConfirm({ avatar: formValue.avatar, user_name: formValue.user_name! }).then(() => {
        navigateTo(null as any)
      })
      return
    }

    await handleRegister(
      {
        user_name: formValue.user_name?.length ? formValue.user_name : context.user?.address,
        avatar: formValue.avatar,
      },
      referral ? referral?.address : undefined,
      campaign || null
    )
  }

  const { mutate, isError, isPending } = useMutation({
    mutationFn: async () => {
      if (!getValues().agreement) {
        setValue('agreement', true)
      }

      clearErrors()

      await handleFormSumbit()
    },
  })

  if (isProcessing) {
    const walletId = initialFields?.wallet || context.wallet

    return (
      <StatusView
        error={isError}
        isSuccess={false}
        backCallback={() => setProcessing(false)}
        retryCallback={() => mutate()}
        provider={walletId || ''}
        operationType={walletId?.includes('social') ? OperationType.SocialSigning : OperationType.WalletSigning}
      />
    )
  }

  return (
    <Flex flexDir={'column'} justifyContent={'space-between'} h={'full'}>
      <Box>
        <StageTitle
          title={t('signin.register')}
          backCallback={withoutBackButton ? undefined : () => navigateTo('SignIn')}
        />
        <Text fontSize="sm" fontWeight={700} color="ninja.300" mb={3}>
          {t('account.form.avatar.label')}*
        </Text>
        <Flex minH={'112px'} mb={4} gap={{ base: 2 }} rounded={'full'} alignItems={'center'}>
          <EditorAvatarComponent
            src={avatar}
            onAvatarChange={(value) => setValue('avatar', value, { shouldValidate: true, shouldDirty: true })}
          />
        </Flex>
        <Box>
          <FormControl isInvalid={errors.user_name ? true : false}>
            <Text fontWeight={600} mb={2} color="ninja.300" fontSize={14}>
              {t('account.form.nickname.label')}*
            </Text>
            <Input
              id="nickname"
              {...register('user_name')}
              placeholder={t('account.form.nickname.placeholder')}
              h={'50px'}
              outline={'none'}
              border={'1px solid transparetn'}
              rounded={12}
              bg="ninja.500"
              _hover={{
                bg: 'ninja.400',
              }}
              _focusVisible={{
                outline: 'none',
                border: 'none',
              }}
              _active={{
                outline: 'none',
                border: 'none',
              }}
              color={'brand.white'}
              _placeholder={{
                color: 'ninja.300',
              }}
              fontWeight={700}
              fontSize={'sm'}
            />
          </FormControl>
          {!!errors.user_name && (
            <Text mt={1} fontSize={'xs'} fontWeight={700} color={'brand.error'}>
              {errors.user_name.message}
            </Text>
          )}
        </Box>
        <Flex gap={4} mt={4} mb="4" alignItems={'start'}>
          <Box mt={1}>
            <Checkbox {...register('agreement')} />
          </Box>
          <Text fontSize="xs" color="ninja.200">
            <Trans
              i18nKey="SignIn.agreement"
              components={{
                PrivacyLink: (
                  <Link
                    color={'ninja.100'}
                    textDecor={'underline'}
                    href="https://docs.blaze.ninja/additional/privacy-policy"
                    isExternal
                  />
                ),
                TermsLink: (
                  <Link
                    color={'ninja.100'}
                    textDecor={'underline'}
                    href="https://docs.blaze.ninja/additional/terms-and-conditions"
                    isExternal
                  />
                ),
              }}
            />
          </Text>
        </Flex>
      </Box>
      <Button
        isDisabled={!isValid || !!Object.keys(errors).length}
        isLoading={isPending}
        onClick={() => mutate()}
        colorScheme="blue"
        size="lg"
        className="block w-full"
        w={'full'}
      >
        {t('common.button.sign-in')}
      </Button>
    </Flex>
  )
}

const getUserParams = (profile?: Profile) => {
  const details = (profile?.details as any) || {}

  if (profile?.type === 'google') {
    return {
      avatar: details.picture,
      user_name: details.email?.split('@')?.at(0),
    }
  }

  if (profile?.type === 'discord') {
    return {
      avatar: details.avatar,
      user_name: details.username,
    }
  }

  if (profile?.type === 'x') {
    return {
      avatar: details.avatar,
      user_name: details.username,
    }
  }

  if (profile?.type === 'steam') {
    return {
      avatar: details.avatar,
      user_name: details.username,
    }
  }

  if (profile?.type === 'telegram') {
    return {
      avatar: details.picture,
      user_name: details.username,
    }
  }

  if (profile?.type === 'facebook') {
    return {
      avatar: details.picture,
      user_name: details.email?.split('@')?.at(0),
    }
  }
}

const parseUsername = (username: string) => {
  const copy = username

  return username
    .split('')
    .map((char, index) => {
      const code = copy.charCodeAt(index)

      if (char === '$') {
        return ''
      }

      if (code < 0x21 || code > 0x7e) {
        return '?'
      }

      return char
    })
    .join('')
    .replaceAll(' ', '')
    .padEnd(3, '0')
    .slice(0, 12)
}
